import { useState } from "react";
import { Download } from "lucide-react";
import { PageHeader } from "../../../components/PortfolioPerformance/page-header";
import { Panel } from "../../../components/PortfolioPerformance/panel";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { Skeleton } from "../../../ui/skeleton";
import { Pager } from "../../../components/PortfolioPerformance/pager";
import { downloadEpdContracts, useEpd, useEpdContracts, useEpdRate, type EpdStatus, type EpdTrend } from "../lib/api/epd";

const STATUS_VARIANT: Record<string, "destructive" | "outline"> = {
  Bankruptcy: "destructive",
  Default: "destructive",
  "90+ Delinquent": "outline",
  "60+ Delinquent": "outline",
};

/** One change line under a card: ▲ / ▼ / – , the %, and what it is measured against. Fewer early payment defaults is good news, so down is
 *  green and up is red; "n/a" (nothing to compare against) and no change are grey. */
function TrendLine({ trend, label }: { trend: EpdTrend; label: string }) {
  const colour =
    trend.value === "n/a" || trend.direction === "flat"
      ? "text-muted-foreground"
      : trend.direction === "down"
        ? "text-approve"
        : "text-decline";
  const arrow = trend.value === "n/a" ? "–" : trend.direction === "down" ? "▼" : trend.direction === "up" ? "▲" : "–";
  return (
    <p className={`mt-1 text-xs font-medium tabular-nums ${colour}`}>
      {arrow} {trend.value} {label}
    </p>
  );
}

export default function EpdPage() {
  const { data, isPending, isError, error } = useEpd();
  const [offset, setOffset] = useState(0);
  const contracts = useEpdContracts(offset);
  const rate = useEpdRate();
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);

  async function handleDownload() {
    setDownloading(true);
    setDownloadFailed(false);
    try {
      await downloadEpdContracts();
    } catch {
      setDownloadFailed(true);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Early Payment Defaults"
        subtitle="Delinquency, default, or bankruptcy surfacing within roughly the first 6 months of a contract's life, a signal on origination quality, not seasoning."
        asOfPage="epd"
      />

      {isError ? (
        <div
          role="alert"
          className="mt-8 border border-decline/50 bg-decline/10 p-5 text-sm text-foreground"
        >
          <p className="font-semibold">Could not load EPD data.</p>
          <p className="mt-1 text-muted-foreground">
            {error instanceof Error ? error.message : "Unknown failure."}
          </p>
        </div>
      ) : isPending ? (
        <div className="mt-8 space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : data === null ? (
        <div className="mt-8 border border-border bg-card p-5 text-sm text-muted-foreground">
          Not Available Yet.
        </div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="border border-border bg-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                EPD Contracts (TTM)
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                {data.contractsTTM}
              </p>
              {/* Last month first, the year comparison below it. */}
              {data.contractsTrendMonth ? <TrendLine trend={data.contractsTrendMonth} label={`vs. last month${data.vsMonth ? ` (${data.vsMonth})` : ""}`} /> : null}
              <TrendLine trend={data.contractsTrend} label={`vs. same month last year${data.vsYear ? ` (${data.vsYear})` : ""}`} />
            </div>
            <div className="border border-border bg-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                EPD Exposure (TTM)
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                {data.exposureTTM}
              </p>
              {data.exposureTrend ? <TrendLine trend={data.exposureTrend} label={`vs. same month last year${data.vsYear ? ` (${data.vsYear})` : ""}`} /> : null}
            </div>
            <div className="border border-border bg-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                EPD Rate (% of Bookings)
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                {rate.isError ? "n/a" : rate.isPending ? "…" : (rate.data?.ratePctOfBookings ?? "n/a")}
              </p>
              {rate.data?.rateTrend ? (
                <TrendLine
                  trend={rate.data.rateTrend}
                  label={`vs. same month last year${(rate.data.vsYear ?? data.vsYear) ? ` (${rate.data.vsYear ?? data.vsYear})` : ""}`}
                />
              ) : null}
            </div>
          </div>

          <Panel
            className="mt-6"
            title="EPD Contracts"
            description="Contracts flagged at the latest reporting period, largest exposure first"
            right={
              <div className="flex items-center gap-3">
                {downloadFailed ? <span className="text-xs text-destructive">Download failed</span> : null}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  disabled={downloading}
                  onClick={handleDownload}
                >
                  <Download className="size-3.5" />
                  {downloading ? "Downloading…" : "Download CSV"}
                </Button>
              </div>
            }
          >
            {contracts.isError ? (
              <p className="text-sm text-destructive">Could not load this table.</p>
            ) : !contracts.data ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="pb-2">Contract</th>
                    <th className="pb-2">Office</th>
                    <th className="pb-2">Industry</th>
                    <th className="pb-2 text-right">Months on Book</th>
                    <th className="pb-2 pl-6">Status</th>
                    <th className="pb-2 text-right">Exposure</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.data.items.map((c) => (
                    <tr key={c.contract} className="border-t border-border">
                      <td className="py-2 font-medium text-foreground">{c.contract}</td>
                      <td className="py-2">{c.office}</td>
                      <td className="py-2">{c.industry}</td>
                      <td className="py-2 text-right tabular-nums">{c.monthsOnBook}</td>
                      <td className="py-2 pl-6">
                        <Badge variant={STATUS_VARIANT[c.status] ?? "outline"}>{c.status}</Badge>
                      </td>
                      <td className="py-2 text-right tabular-nums">{c.exposure}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
                <Pager
                  offset={offset}
                  total={contracts.data.total}
                  isFetching={contracts.isFetching}
                  onChange={setOffset}
                />
              </>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
